/**
 * DATA LAYER (DL): PERSONAL NOISEMETER (GRITO MEXICANO)
 * Manejo de peticiones HTTP REST hacia los endpoints administrativos y públicos de Grito Mexicano.
 */

const getEndpointUrl = (subPath) => {
    let base = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    if (!base.endsWith('/')) {
        base += '/';
    }
    const cleanSub = subPath.startsWith('/') ? subPath.slice(1) : subPath;
    if (base.endsWith('/api/')) {
        if (cleanSub.startsWith('api/')) {
            return `${base}${cleanSub.slice(4)}`;
        }
        return `${base}${cleanSub}`;
    } else {
        if (cleanSub.startsWith('api/')) {
            return `${base}${cleanSub}`;
        }
        return `${base}api/${cleanSub}`;
    }
};

const getAuthHeaders = (isJson = true) => {
    const token = localStorage.getItem('interaktia_token');
    const headers = {};
    if (isJson) {
        headers['Content-Type'] = 'application/json';
    }
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};

/**
 * Obtiene la configuración administrativa completa de Grito Mexicano para una sala.
 */
export const GetAdminConfig = async (roomId = null) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        const url = `${getEndpointUrl('api/v2/admin/personal-noisemeter/config')}${query.toString() ? `?${query.toString()}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al obtener la configuración de administración.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al consultar la configuración administrativa.' };
    }
};

/**
 * Actualiza la configuración administrativa de la dinámica (duración, colores, textos, etc.).
 */
export const UpdateAdminConfig = async (payload, roomId = null) => {
    try {
        const dataToSend = {
            ...payload,
            ...(roomId && !payload.roomId ? { roomId } : {})
        };
        const url = getEndpointUrl('api/v2/admin/personal-noisemeter/config');

        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(dataToSend)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al guardar la configuración.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al actualizar la configuración.' };
    }
};

/**
 * Obtiene la lista paginada y filtrada de participantes para la mesa de control del operador.
 */
export const GetAdminParticipants = async (roomId = null, search = '', status = '', page = 1, pageSize = 50) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        if (search && search.trim()) query.append('search', search.trim());
        if (status && status !== 'all' && status.trim()) query.append('status', status.trim());
        if (page) query.append('page', page);
        if (pageSize) query.append('pageSize', pageSize);

        const url = `${getEndpointUrl('api/v2/admin/personal-noisemeter/participants')}${query.toString() ? `?${query.toString()}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al consultar la lista de participantes.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al consultar los participantes.' };
    }
};

/**
 * Reactiva a un participante archivando su intento anterior y emitiendo evento SignalR.
 */
export const ResetParticipant = async (payload) => {
    try {
        const url = getEndpointUrl('api/v2/admin/personal-noisemeter/reset-participant');
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al reactivar participante.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al reactivar participante.' };
    }
};

/**
 * Descalifica o reincorpora a un participante y actualiza la tabla de clasificación.
 */
export const DisqualifyParticipant = async (payload) => {
    try {
        const url = getEndpointUrl('api/v2/admin/personal-noisemeter/disqualify-participant');
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al modificar estatus de descalificación.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al descalificar/reincorporar participante.' };
    }
};

/**
 * Descarga el archivo CSV con el reporte completo de participantes ordenados por ranking oficial.
 */
export const ExportParticipantsCsv = async (roomId = null) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        const url = `${getEndpointUrl('api/v2/admin/personal-noisemeter/export-participants')}${query.toString() ? `?${query.toString()}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(false),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al descargar el archivo CSV.' };
        }

        const blob = await response.blob();
        return { success: true, data: blob };
    } catch (error) {
        return { success: false, message: 'Error de red al exportar el reporte CSV.' };
    }
};

/**
 * Obtiene la configuración pública de Grito Mexicano para los clientes web móviles.
 */
export const GetPublicConfig = async (roomId = null) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        const url = `${getEndpointUrl('api/v2/personal-noisemeter/config')}${query.toString() ? `?${query.toString()}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al obtener la configuración pública.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al obtener configuración pública.' };
    }
};

/**
 * Registra a un nuevo aficionado y genera su token OTT de 5 minutos.
 */
export const RegisterParticipant = async (payload) => {
    try {
        const url = getEndpointUrl('api/v2/personal-noisemeter/register');
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        if (!response.ok) {
            return { success: false, message: data.message || 'Error en el registro del participante.', data };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al registrar participante.' };
    }
};

/**
 * Valida el token OTT y envía la telemetría del grito calculando el puntaje oficial.
 */
export const SubmitScore = async (payload) => {
    try {
        const url = getEndpointUrl('api/v2/personal-noisemeter/submit-score');
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        const data = await response.json();
        if (!response.ok) {
            return { success: false, message: data.message || 'Error al registrar puntuación.', data };
        }

        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al enviar la puntuación del grito.' };
    }
};

/**
 * Obtiene el ranking oficial en tiempo real con nombres enmascarados y estadísticas.
 */
export const GetRanking = async (roomId = null, top = 20) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        if (top) query.append('top', top);

        const url = `${getEndpointUrl('api/v2/personal-noisemeter/ranking')}${query.toString() ? `?${query.toString()}` : ''}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al consultar ranking.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al consultar el ranking.' };
    }
};

/**
 * Consulta el estado actual de un participante en la sala (actividad, reactivación, score y permiso de múltiples registros).
 */
export const GetParticipantStatus = async (roomId, participantId) => {
    try {
        if (!participantId) {
            return { success: false, message: 'ParticipantId es obligatorio.' };
        }

        const query = new URLSearchParams();
        query.append('participantId', participantId);
        if (roomId) query.append('roomId', roomId);

        const url = `${getEndpointUrl('api/v2/personal-noisemeter/participant-status')}?${query.toString()}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al consultar estado del participante.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al verificar el estado del participante.' };
    }
};

/**
 * Registra un evento de telemetría y embudo de conversión para la dinámica.
 */
export const TrackMetric = async (payload, roomId = null) => {
    try {
        const dataToSend = {
            ...payload,
            ...(roomId && !payload.roomId ? { roomId } : {})
        };
        const url = getEndpointUrl('api/v2/personal-noisemeter/track');

        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataToSend)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al registrar métrica de telemetría.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al registrar métrica.' };
    }
};

/**
 * Obtiene las estadísticas de telemetría, embudo de conversión y fuentes para la sala.
 */
export const GetAdminStats = async (roomId = null) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        const url = `${getEndpointUrl('api/v2/admin/personal-noisemeter/stats')}${query.toString() ? `?${query.toString()}` : ''}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al consultar estadísticas del embudo.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al consultar estadísticas del embudo.' };
    }
};

/**
 * Alterna el estado de activación de registros de la dinámica (abrir o pausar participación).
 */
export const ToggleActivePersonalNoisemeter = async (isActive, roomId = null) => {
    try {
        const payload = {
            isActive,
            ...(roomId ? { roomId } : {})
        };
        const url = getEndpointUrl('api/v2/admin/personal-noisemeter/toggle-active');

        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al actualizar el estado de la dinámica.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al actualizar el estado de la dinámica.' };
    }
};

/**
 * Reinicia por completo la dinámica en la sala (elimina participantes, puntuaciones y telemetría).
 */
export const ResetPersonalNoisemeterData = async (roomId = null) => {
    try {
        const query = new URLSearchParams();
        if (roomId) query.append('roomId', roomId);
        const url = `${getEndpointUrl('api/v2/admin/personal-noisemeter/reset-data')}${query.toString() ? `?${query.toString()}` : ''}`;

        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return { success: false, message: errorData.message || 'Error al reiniciar los datos de la dinámica.' };
        }

        const data = await response.json();
        return { success: true, data };
    } catch (error) {
        return { success: false, message: 'Error de red al reiniciar la dinámica.' };
    }
};
